import axios from "axios";
import { toast } from "react-toastify";
import { encrypted, tokenManager } from "../utils/utility";
import { handleUnauthorized } from "../utils/apiHelper";
import { ENCRYPTION } from "../constant/globalConstants";
import { env } from "../environment";

// Service configurations
const SERVICE_CONFIG = {
  default: {
    baseUrl: env.baseURL,
    tokenKey: "token",
  },
  aichat: {
    baseUrl: env.aichatBaseURL,
    tokenKey: "token",
  },
  aichatExcel: {
    baseUrl: env.aichatExcelBaseURL,
    tokenKey: "token",
  },
  productLabelAI: {
    baseUrl: env.productLabelBaseURL,
    tokenKey: "token",
  },
  translate: {
    baseUrl: env.translateBaseURL,
    tokenKey: "token",
  },
  procurement: {
    baseUrl: env.procurementBaseURL,
    tokenKey: "token",
  },
  gmt: {
    baseUrl: env.GMTBaseURL,
    tokenKey: "token",
  },
  deepInsights: {
    baseUrl: env.deepInsightsURL,
    tokenKey: "token",
  },
  talentPulse: {
    baseUrl: env.talentPulseBaseURL,
    tokenKey: "token",
  }
};

/**
 * Determines the service type from options
 */
const getServiceType = (options = {}) => {
  if (options.useAIChatURL) return "aichat";
  if (options.useAIChatExcelURL) return "aichatExcel";
  if (options.useProductLabelAIURL) return "productLabelAI";
  if (options.useTranslateURL) return "translate";
  if (options.useProcurementURL) return "procurement";
  if (options.useGMTURL) return "gmt";
  if (options.useDeepInsightURL) return "deepInsights";
  if (options.useTalentPulseURL) return "talentPulse";
  return "default";
};

/**
 * Creates authorization headers for a given service
 */
const createAuthHeaders = (serviceType) => {
  const config = SERVICE_CONFIG[serviceType];
  const token = tokenManager.getToken(config.tokenKey);
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

/**
 * Builds the full URL for a request
 */
const buildUrl = (endpoint, serviceType) => {
  const config = SERVICE_CONFIG[serviceType];
  return `${config.baseUrl}${endpoint}`;
};

/**
 * Encrypts the request body
 */
const encryptBody = (body) => {
  return { encryptedBody: encrypted.encrypt(JSON.stringify(body)) };
};

/**
 * Decrypts the response body
 */
const decryptResponse = (data) => {
  return JSON.parse(encrypted.decrypt(data?.encryptedBody));
};

/**
 * Formats error messages
 */
const formatErrorMessage = (error) => {
  if (error?.message?.includes("timeout")) {
    return "Request timed out";
  }
  return error.response?.data?.message || error.message;
};

/**
 * Main axios base query function
 * @param {Object} args - Request arguments
 * @param {string} args.url - The endpoint URL
 * @param {string} args.method - HTTP method (GET, POST, etc.)
 * @param {Object} args.body - Request body (for POST)
 * @param {Object} args.params - Query parameters
 * @param {Object} args.options - Additional options
 * @returns {Promise} - Response data or error
 */
export const axiosBaseQuery = async ({
  url,
  method = "GET",
  body = null,
  params = null,
  options = {},
}) => {
  const serviceType = getServiceType(options);
  const fullUrl = buildUrl(url, serviceType);
  const isFormData = body instanceof FormData;

  // Create headers
  const headers = options.skipAuth ? { "Content-Type": "application/json" } : createAuthHeaders(serviceType);

  // Remove Content-Type for FormData (browser sets it with boundary)
  if (isFormData) {
    delete headers["Content-Type"];
  }

  // Setup cancel token
  const source = options?.cancelTokenSource || axios.CancelToken.source();

  const config = {
    headers,
    params: params || {},
    cancelToken: source.token,
  };

  if (options.responseType) {
    config.responseType = options.responseType;
  }

  // Setup timeout (15 minutes)
  const timeoutId = setTimeout(() => {
    source.cancel("Request timed out after 15 minutes");
  }, 900000);

  try {
    // Use global ENCRYPTION constant to determine if encryption/decryption is enabled
    const enableEncryption = ENCRYPTION;

    // Prepare payload
    const payload = isFormData || !enableEncryption ? body : encryptBody(body);

    // Make request
    const response =
      method.toUpperCase() === "POST"
        ? await axios.post(fullUrl, payload, config)
        : await axios.get(fullUrl, config);

    clearTimeout(timeoutId);

    // Process response
    let data;
    if (options.responseType === 'blob' || options.responseType === 'arraybuffer') {
      data = response.data;
    } else {
      data = enableEncryption && response.data?.encryptedBody
        ? decryptResponse(response.data)
        : response.data;
    }

    if (data && data.status === false) {
      const error = new Error(data.message || "Operation failed");
      error.status = response.status;
      Object.assign(error, data);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    clearTimeout(timeoutId);

    // Handle cancelled requests
    if (axios.isCancel(error)) {
      return { data: null, error, cancelled: true };
    }

    // Handle 401
    if (error?.response?.status === 401) {
      handleUnauthorized();
    }
    // Handle 504
    if (error?.response?.status === 504) {
      console.error("Gateway Timeout:", error);
      toast.error("The server is taking too long to respond. Please try again later.");
    }

    // Handle 422 – backend may return a structured body (e.g. needs_input).
    // When encryption is on the raw response data is an encrypted blob, so we
    // attempt to decrypt it before reading the message field.
    if (error?.response?.status === 422) {
      let responseData = error.response?.data;
      if (responseData?.encryptedBody) {
        try {
          responseData = decryptResponse(responseData);
        } catch {
          // If decryption fails keep the raw data
        }
      }
      const message =
        responseData?.message ||
        responseData?.clarification ||
        "Invalid request. Please check your inputs.";
      const customError = new Error(message);
      customError.status = 422;
      customError.responseData = responseData;
      customError.originalError = error;
      return { data: null, error: customError };
    }

    const customError = new Error(formatErrorMessage(error));
    customError.status = error?.response?.status;
    customError.originalError = error;
    return { data: null, error: customError };
  }
};

export {
  SERVICE_CONFIG,
  getServiceType,
  buildUrl,
  encryptBody,
  decryptResponse
};



